import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { login as apiLogin, logout as apiLogout, getMyProfile, getMyFeatures } from '../api/client'
import i18n from '../i18n'

interface AuthCtx {
  token: string | null
  username: string | null
  fullName: string | null
  position: string | null
  email: string | null
  roles: string[]
  permissions: string[]
  departments: string[]
  isSuperAdmin: boolean
  companyId: number | null
  companyCode: string | null
  companyName: string | null
  companyLogoUrl: string | null
  companyThemeColor: string | null
  enabledFeatures: string[]
  featuresLoaded: boolean
  hasFeature: (key: string) => boolean
  allowedPages: string[] | null   // null = ไม่จำกัด (ADMIN), string[] = จำกัด
  hasPageAccess: (key: string) => boolean
  doLogin: (u: string, p: string) => Promise<void>
  doLogout: () => void
  hasPermission: (perm: string) => boolean
  updateBranding: (b: { companyName?: string; companyLogoUrl?: string; companyThemeColor?: string }) => void
}

const Ctx = createContext<AuthCtx>(null!)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken]             = useState<string | null>(localStorage.getItem('token'))
  const [username, setUsername]       = useState<string | null>(localStorage.getItem('username'))
  const [fullName, setFullName]       = useState<string | null>(localStorage.getItem('fullName'))
  const [position, setPosition]       = useState<string | null>(localStorage.getItem('position'))
  const [email, setEmail]             = useState<string | null>(localStorage.getItem('email'))
  const [roles, setRoles]             = useState<string[]>(JSON.parse(localStorage.getItem('roles') || '[]'))
  const [permissions, setPermissions] = useState<string[]>(JSON.parse(localStorage.getItem('permissions') || '[]'))
  const [departments, setDepartments] = useState<string[]>(JSON.parse(localStorage.getItem('departments') || '[]'))
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(localStorage.getItem('isSuperAdmin') === 'true')
  const [companyId, setCompanyId]     = useState<number | null>(
    localStorage.getItem('companyId') ? Number(localStorage.getItem('companyId')) : null
  )
  const [companyCode, setCompanyCode] = useState<string | null>(localStorage.getItem('companyCode'))
  const [companyName, setCompanyName] = useState<string | null>(localStorage.getItem('companyName'))
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(localStorage.getItem('companyLogoUrl'))
  const [companyThemeColor, setCompanyThemeColor] = useState<string | null>(localStorage.getItem('companyThemeColor'))
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>(
    JSON.parse(localStorage.getItem('enabledFeatures') || '[]')
  )
  // true ถ้าโหลด features จาก API แล้ว หรือ superadmin (ไม่ต้องโหลด)
  const [featuresLoaded, setFeaturesLoaded] = useState<boolean>(
    localStorage.getItem('isSuperAdmin') === 'true' ||
    localStorage.getItem('enabledFeatures') !== null
  )
  const [allowedPages, setAllowedPages] = useState<string[] | null>(
    localStorage.getItem('allowedPages') === 'null' ? null :
    JSON.parse(localStorage.getItem('allowedPages') || 'null')
  )

  const hasFeature = (key: string) => {
    // superadmin เห็นทุก feature เสมอ
    if (localStorage.getItem('isSuperAdmin') === 'true') return true
    // ยังไม่โหลด = แสดงทุกอย่างไว้ก่อน (ป้องกัน flash หายตอน mount)
    if (!featuresLoaded) return true
    return enabledFeatures.includes(key)
  }

  const hasPageAccess = (key: string) => {
    if (localStorage.getItem('isSuperAdmin') === 'true') return true
    if (allowedPages === null) return true  // null = ไม่จำกัด (ADMIN)
    return allowedPages.includes(key)
  }

  // Fetch latest profile on every app load — always gets updated position/name
  useEffect(() => {
    if (!token) return
    getMyProfile().then(res => {
      const p = res.data
      if (p.fullName !== undefined) {
        setFullName(p.fullName ?? null)
        localStorage.setItem('fullName', p.fullName ?? '')
      }
      if (p.position !== undefined) {
        setPosition(p.position ?? null)
        localStorage.setItem('position', p.position ?? '')
      }
      if (p.email !== undefined) {
        setEmail(p.email ?? null)
        localStorage.setItem('email', p.email ?? '')
      }
    }).catch(() => {})
  }, [token])

  const doLogin = async (u: string, p: string) => {
    const { data } = await apiLogin(u, p)
    const { access_token, user } = data
    localStorage.setItem('token',       access_token)
    localStorage.setItem('username',    user.username)
    localStorage.setItem('fullName',    user.fullName ?? '')
    localStorage.setItem('position',    user.position ?? '')
    localStorage.setItem('email',       user.email ?? '')
    localStorage.setItem('roles',       JSON.stringify(user.roles ?? []))
    localStorage.setItem('permissions', JSON.stringify(user.permissions ?? []))
    localStorage.setItem('departments', JSON.stringify(user.departments ?? []))
    localStorage.setItem('isSuperAdmin', String(!!user.isSuperAdmin))
    localStorage.setItem('companyId',   user.companyId != null ? String(user.companyId) : '')
    localStorage.setItem('companyCode', user.companyCode ?? '')
    localStorage.setItem('companyName', user.companyName ?? '')
    localStorage.setItem('companyLogoUrl', user.companyLogoUrl ?? '')
    localStorage.setItem('companyThemeColor', user.companyThemeColor ?? '')
    localStorage.setItem('enabledFeatures', JSON.stringify(user.enabledFeatures ?? []))
    localStorage.setItem('allowedPages', user.allowedPages === null ? 'null' : JSON.stringify(user.allowedPages ?? []))
    const lang = user.preferredLanguage ?? localStorage.getItem('lang') ?? 'th'
    localStorage.setItem('lang', lang)
    i18n.changeLanguage(lang)
    setToken(access_token)
    setUsername(user.username)
    setFullName(user.fullName ?? null)
    setPosition(user.position ?? null)
    setEmail(user.email ?? null)
    setRoles(user.roles ?? [])
    setPermissions(user.permissions ?? [])
    setDepartments(user.departments ?? [])
    setIsSuperAdmin(!!user.isSuperAdmin)
    setCompanyId(user.companyId ?? null)
    setCompanyCode(user.companyCode ?? null)
    setCompanyName(user.companyName ?? null)
    setCompanyLogoUrl(user.companyLogoUrl ?? null)
    setCompanyThemeColor(user.companyThemeColor ?? null)
    setEnabledFeatures(user.enabledFeatures ?? [])
    setFeaturesLoaded(true)
    setAllowedPages(user.allowedPages ?? null)
  }

  const doLogout = () => {
    // best-effort บันทึก Audit Log ว่า logout เมื่อไหร่ - ไม่รอผลลัพธ์ ไม่ให้ผู้ใช้รู้สึกหน่วง
    // และไม่ block การ logout แม้ request จะพลาด (เช่น เน็ตหลุดตอนนั้นพอดี)
    apiLogout().catch(() => {})
    localStorage.clear()
    setToken(null); setUsername(null); setFullName(null)
    setPosition(null); setEmail(null)
    setRoles([]); setPermissions([]); setDepartments([])
    setIsSuperAdmin(false); setCompanyId(null); setCompanyCode(null)
    setCompanyName(null); setCompanyLogoUrl(null); setCompanyThemeColor(null)
    setEnabledFeatures([]); setFeaturesLoaded(false); setAllowedPages(null)
  }

  const hasPermission = (perm: string) => permissions.includes(perm)

  // ให้หน้า Company Profile อัปเดต branding (โลโก้/สี theme) ให้เห็นผลทันทีโดยไม่ต้อง logout/login ใหม่
  const updateBranding = (b: { companyName?: string; companyLogoUrl?: string; companyThemeColor?: string }) => {
    if (b.companyName !== undefined) {
      localStorage.setItem('companyName', b.companyName)
      setCompanyName(b.companyName || null)
    }
    if (b.companyLogoUrl !== undefined) {
      localStorage.setItem('companyLogoUrl', b.companyLogoUrl)
      setCompanyLogoUrl(b.companyLogoUrl || null)
    }
    if (b.companyThemeColor !== undefined) {
      localStorage.setItem('companyThemeColor', b.companyThemeColor)
      setCompanyThemeColor(b.companyThemeColor || null)
    }
  }

  return (
    <Ctx.Provider value={{
      token, username, fullName, position, email,
      roles, permissions, departments,
      isSuperAdmin, companyId, companyCode, companyName, companyLogoUrl, companyThemeColor,
      enabledFeatures, featuresLoaded, hasFeature,
      allowedPages, hasPageAccess,
      doLogin, doLogout, hasPermission, updateBranding,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export const useAuth = () => useContext(Ctx)
